import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getUser, logout } from '../../services/helpers'
import { updateData, postData, getData } from '../../services/request'
import { getAPIBaseURL } from '../../services/helpers'
import {
  ChartBarIcon,
  CalendarIcon,
  PlusCircleIcon,
  TagIcon,
  ShoppingBagIcon,
  TicketIcon,
  WalletIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  Bars3Icon,
  XMarkIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  BanknotesIcon,
  CheckCircleIcon,
  CameraIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  PhotoIcon
} from '@heroicons/react/24/outline'

const CenterProfilePage = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState(getUser())
  const displayName = user?.organization || user?.name || 'Event Center'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('info')
  
  // Password change modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordMessage, setPasswordMessage] = useState('')
  const [passwordMessageType, setPasswordMessageType] = useState('info')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  
  const [editing, setEditing] = useState(false)
  const [countries, setCountries] = useState([])
  const [loadingCountries, setLoadingCountries] = useState(true)
  const [profile, setProfile] = useState({
    organization: user?.organization || '',
    email: user?.email || '',
    phoneNumber: user?.phoneNumber || '',
    country: user?.country || '',
    city: user?.city || '',
    address: user?.address || '',
    accountNumber: user?.accountNumber || '',
    accountName: user?.accountName || '',
    bank: user?.bank || '',
    iban: user?.iban || '',
    routingNumber: user?.routingNumber || ''
  })
  const [originalProfile, setOriginalProfile] = useState({
    organization: user?.organization || '',
    email: user?.email || '',
    phoneNumber: user?.phoneNumber || '',
    country: user?.country || '',
    city: user?.city || '',
    address: user?.address || '',
    accountNumber: user?.accountNumber || '',
    accountName: user?.accountName || '',
    bank: user?.bank || '',
    iban: user?.iban || '',
    routingNumber: user?.routingNumber || ''
  })

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const url = `${getAPIBaseURL()}/v1/countries`
        const res = await getData(url, {}, {})
        if (res.error === '' && res.result?.status === 200) {
          const list = res.result.data?.data || res.result.data || []
          setCountries(Array.isArray(list) ? list : [])
        }
      } catch (err) {
        // silently fail
      } finally {
        setLoadingCountries(false)
      }
    }
    fetchCountries()
  }, [])

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon, path: '/center' },
    { id: 'events', label: 'My Events', icon: CalendarIcon, path: '/center/events', disabled: true },
    { id: 'offers', label: 'My Offers', icon: TagIcon, path: '/center/offers' },
    { id: 'orders', label: 'Orders', icon: ShoppingBagIcon, path: '/center/orders' },
    { id: 'tickets', label: 'Tickets', icon: TicketIcon, path: '/center/tickets' },
    { id: 'media', label: 'Media Gallery', icon: PhotoIcon, path: '/center/media', disabled: true },
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, path: '/center/disputes' },
    { id: 'wallet', label: 'Wallet', icon: WalletIcon, path: '/center/wallet' },
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/center/profile' }
  ]

  const handleEdit = () => {
    setEditing(true)
    setMessage('')
    // Store original values
    setOriginalProfile({ ...profile })
  }

  const handleCancel = () => {
    setEditing(false)
    setMessage('')
    // Restore original values
    setProfile({ ...originalProfile })
  }

  const handleProfileChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value })
  }

  const handleProfileSubmit = async (e) => {
    e.preventDefault()
    if (!editing) return
    
    setLoading(true)
    setMessage('')

    // Use the correct endpoint (same pattern as admin profile update)
    const url = `${getAPIBaseURL()}/v1/users/update-account`
    // Don't pass token in headers - authentication is handled via cookies (withCredentials: true)
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    
    const params = new URLSearchParams()
    // Only append fields that have values
    Object.keys(profile).forEach(key => {
      if (profile[key] && profile[key] !== '') {
        params.append(key, profile[key])
      }
    })

    const res = await updateData(url, headers, params.toString())
    if (res.error === '') {
      if (res.result?.status === 200) {
        setMessage('Profile updated successfully!')
        setMessageType('success')
        // Get updated user data from response
        const updatedUserData = res.result?.data?.data || res.result?.data
        if (updatedUserData) {
          const updatedUser = { ...user, ...updatedUserData }
          localStorage.setItem('user', JSON.stringify(updatedUser))
          setUser(updatedUser)
          // Update profile state with new data
          setProfile({
            organization: updatedUserData.organization || profile.organization || '',
            email: updatedUserData.email || profile.email || '',
            phoneNumber: updatedUserData.phoneNumber || profile.phoneNumber || '',
            country: updatedUserData.country || profile.country || '',
            city: updatedUserData.city || profile.city || '',
            address: updatedUserData.address || profile.address || '',
            accountNumber: updatedUserData.accountNumber || profile.accountNumber || '',
            accountName: updatedUserData.accountName || profile.accountName || '',
            bank: updatedUserData.bank || profile.bank || '',
            iban: updatedUserData.iban || profile.iban || '',
            routingNumber: updatedUserData.routingNumber || profile.routingNumber || ''
          })
          // Update original profile
          setOriginalProfile({
            organization: updatedUserData.organization || profile.organization || '',
            email: updatedUserData.email || profile.email || '',
            phoneNumber: updatedUserData.phoneNumber || profile.phoneNumber || '',
            country: updatedUserData.country || profile.country || '',
            city: updatedUserData.city || profile.city || '',
            address: updatedUserData.address || profile.address || '',
            accountNumber: updatedUserData.accountNumber || profile.accountNumber || '',
            accountName: updatedUserData.accountName || profile.accountName || '',
            bank: updatedUserData.bank || profile.bank || '',
            iban: updatedUserData.iban || profile.iban || '',
            routingNumber: updatedUserData.routingNumber || profile.routingNumber || ''
          })
        }
        setEditing(false)
      } else {
        const errorMsg = res.result?.data?.data || res.result?.data?.message || 'Failed to update profile'
        setMessage(errorMsg)
        setMessageType('error')
      }
    } else {
      const errorMsg = res.result?.data?.data || res.result?.data?.message || 'Failed to update profile. Please try again.'
      setMessage(errorMsg)
      setMessageType('error')
    }
    setLoading(false)
  }

  const handlePasswordChange = async (e) => {
    e.preventDefault()
    setPasswordLoading(true)
    setPasswordMessage('')

    // Validation
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage('New passwords do not match')
      setPasswordMessageType('error')
      setPasswordLoading(false)
      return
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordMessage('Password must be at least 6 characters')
      setPasswordMessageType('error')
      setPasswordLoading(false)
      return
    }

    // Use correct endpoint and don't pass token in headers (authentication via cookies)
    const url = `${getAPIBaseURL()}/v1/users/change-password`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    
    const params = new URLSearchParams()
    // Backend expects 'password' for current password, not 'oldPassword'
    params.append('password', passwordForm.currentPassword)
    params.append('newPassword', passwordForm.newPassword)

    const res = await updateData(url, headers, params.toString())
    if (res.error === '') {
      if (res.result?.status === 200) {
        setPasswordMessage('Password changed successfully!')
        setPasswordMessageType('success')
        setTimeout(() => {
          setShowPasswordModal(false)
          setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
          setPasswordMessage('')
        }, 2000)
      } else {
        const errorMsg = res.result?.data?.data || res.result?.data?.message || 'Failed to change password'
        setPasswordMessage(errorMsg)
        setPasswordMessageType('error')
      }
    } else {
      const errorMsg = res.result?.data?.data || res.result?.data?.message || 'Failed to change password. Please check your current password.'
      setPasswordMessage(errorMsg)
      setPasswordMessageType('error')
    }
    setPasswordLoading(false)
  }

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center text-white shadow-sm">
              <BuildingOfficeIcon className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">Obule Panel</span>
            <button onClick={() => setMobileMenuOpen(false)} className="ml-auto lg:hidden">
              <XMarkIcon className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.filter(tab => !tab.disabled).map((tab) => {
              const Icon = tab.icon
              const isActive = tab.id === 'profile'

              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <UserCircleIcon className="w-6 h-6" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => {
                logout()
                navigate('/center/login')
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
        {/* Top Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 flex items-center justify-between px-4 h-16 sticky top-0 z-40">
          <button onClick={() => setMobileMenuOpen(true)}>
            <Bars3Icon className="w-6 h-6 text-slate-600" />
          </button>
          <span className="font-semibold text-slate-900">Profile</span>
          <div className="w-6" />
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900">Profile Settings</h1>
            <p className="text-slate-600 mt-1">Manage your account information and preferences</p>
          </div>

          {/* Message */}
          {message && (
            <div className={`mb-6 p-4 rounded-xl border ${
              messageType === 'success' 
                ? 'bg-green-50 border-green-200 text-green-800' 
                : 'bg-red-50 border-red-200 text-red-800'
            }`}>
              <div className="flex items-center gap-2">
                {messageType === 'success' ? (
                  <CheckCircleIcon className="w-5 h-5" />
                ) : (
                  <ExclamationTriangleIcon className="w-5 h-5" />
                )}
                <p className="font-medium">{message}</p>
              </div>
            </div>
          )}

          <div className="max-w-4xl">
            {/* Profile Picture Section */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 mb-6">
              <h2 className="text-lg font-bold text-slate-900 mb-6">Profile Picture</h2>
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white shadow-lg">
                    <span className="text-3xl font-bold">{displayName.charAt(0).toUpperCase()}</span>
                  </div>
                  <button className="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full border-2 border-slate-200 flex items-center justify-center text-slate-600 hover:text-emerald-600 hover:border-emerald-600 transition">
                    <CameraIcon className="w-4 h-4" />
                  </button>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{displayName}</h3>
                  <p className="text-sm text-slate-500 mb-3">{user?.email}</p>
                  <button className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition font-medium">
                    Change Picture
                  </button>
                </div>
              </div>
            </div>

            {/* Account Information */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 mb-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-slate-900">Account Information</h2>
                {!editing && (
                  <button
                    type="button"
                    onClick={handleEdit}
                    className="px-4 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition"
                  >
                    Edit Profile
                  </button>
                )}
              </div>
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <div className="flex items-center gap-2">
                        <BuildingOfficeIcon className="w-4 h-4 text-slate-400" />
                        Organization Name
                      </div>
                    </label>
                    <input
                      name="organization"
                      value={profile.organization}
                      onChange={handleProfileChange}
                      disabled={!editing}
                      className={`w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                        !editing ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : 'bg-white'
                      }`}
                      placeholder="Your organization name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <div className="flex items-center gap-2">
                        <EnvelopeIcon className="w-4 h-4 text-slate-400" />
                        Email Address
                      </div>
                    </label>
                    <input
                      name="email"
                      value={profile.email}
                      onChange={handleProfileChange}
                      type="email"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-slate-50"
                      placeholder="your@email.com"
                      disabled
                    />
                    <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <div className="flex items-center gap-2">
                        <PhoneIcon className="w-4 h-4 text-slate-400" />
                        Phone Number
                      </div>
                    </label>
                    <input
                      name="phoneNumber"
                      value={profile.phoneNumber}
                      onChange={handleProfileChange}
                      disabled={!editing}
                      className={`w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                        !editing ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : 'bg-white'
                      }`}
                      placeholder="+234 XXX XXX XXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <div className="flex items-center gap-2">
                        <MapPinIcon className="w-4 h-4 text-slate-400" />
                        Country
                      </div>
                    </label>
                    <select
                      name="country"
                      value={profile.country}
                      onChange={handleProfileChange}
                      disabled={!editing}
                      className={`w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                        !editing ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : 'bg-white'
                      }`}
                    >
                      <option value="">{loadingCountries ? 'Loading...' : 'Select Country'}</option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <div className="flex items-center gap-2">
                        <MapPinIcon className="w-4 h-4 text-slate-400" />
                        City
                      </div>
                    </label>
                    <input
                      name="city"
                      value={profile.city}
                      onChange={handleProfileChange}
                      disabled={!editing}
                      className={`w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                        !editing ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : 'bg-white'
                      }`}
                      placeholder="e.g. Lagos"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <div className="flex items-center gap-2">
                        <MapPinIcon className="w-4 h-4 text-slate-400" />
                        Address
                      </div>
                    </label>
                    <input
                      name="address"
                      value={profile.address}
                      onChange={handleProfileChange}
                      disabled={!editing}
                      className={`w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                        !editing ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : 'bg-white'
                      }`}
                      placeholder="Your address"
                    />
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-100">
                  <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <BanknotesIcon className="w-5 h-5 text-emerald-600" />
                    Bank Details for Payouts
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Bank Name</label>
                      <input
                        name="bank"
                        placeholder="Select your bank"
                        value={profile.bank}
                        onChange={handleProfileChange}
                        disabled={!editing}
                        className={`w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                          !editing ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : 'bg-white'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Account Number</label>
                      <input
                        name="accountNumber"
                        placeholder="0123456789"
                        value={profile.accountNumber}
                        onChange={handleProfileChange}
                        disabled={!editing}
                        className={`w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                          !editing ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : 'bg-white'
                        }`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Account Name</label>
                      <input
                        name="accountName"
                        placeholder="Account holder name"
                        value={profile.accountName}
                        onChange={handleProfileChange}
                        disabled={!editing}
                        className={`w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                          !editing ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : 'bg-white'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">IBAN (Optional)</label>
                      <input
                        name="iban"
                        placeholder="GB82 WEST 1234 5698 7654 32"
                        value={profile.iban}
                        onChange={handleProfileChange}
                        disabled={!editing}
                        className={`w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                          !editing ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : 'bg-white'
                        }`}
                      />
                      <p className="text-xs text-slate-500 mt-1">International Bank Account Number</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Routing Number (Optional)</label>
                      <input
                        name="routingNumber"
                        placeholder="123456789"
                        value={profile.routingNumber}
                        onChange={handleProfileChange}
                        disabled={!editing}
                        maxLength={9}
                        className={`w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                          !editing ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : 'bg-white'
                        }`}
                      />
                      <p className="text-xs text-slate-500 mt-1">9-digit routing number (US/Canada)</p>
                    </div>
                  </div>
                </div>

                {editing && (
                  <div className="flex gap-4 pt-6">
                    <button
                      type="button"
                      onClick={handleCancel}
                      disabled={loading}
                      className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="w-5 h-5" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                )}
              </form>
            </div>

            {/* Security Section */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <LockClosedIcon className="w-5 h-5 text-emerald-600" />
                Security
              </h2>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div>
                  <h3 className="font-semibold text-slate-900">Password</h3>
                  <p className="text-sm text-slate-500">Keep your account secure</p>
                </div>
                <button 
                  onClick={() => setShowPasswordModal(true)}
                  className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition font-medium"
                >
                  Change Password
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <LockClosedIcon className="w-6 h-6 text-emerald-600" />
                Change Password
              </h2>
              <button
                onClick={() => {
                  setShowPasswordModal(false)
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
                  setPasswordMessage('')
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {passwordMessage && (
              <div className={`mb-4 p-3 rounded-lg border ${
                passwordMessageType === 'success' 
                  ? 'bg-green-50 border-green-200 text-green-800' 
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}>
                <p className="text-sm font-medium">{passwordMessage}</p>
              </div>
            )}

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Enter current password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Enter new password"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Confirm new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showConfirmPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false)
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
                    setPasswordMessage('')
                  }}
                  className="flex-1 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {passwordLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Changing...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-5 h-5" />
                      Change Password
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default CenterProfilePage

